import React, { createContext, useContext, useMemo } from 'react'
import { Comment, CommentAnchorType } from '@marketing-milk/interfaces'
import { buildUnresolvedCountMap, unresolvedCountKey } from 'components/comments/commentCounts'

/**
 * Packet-wide unresolved-comment counts (v1.4a). The hydrated packet ships every
 * anchor's comments in one flat list (`SprintPacketHydrated.comments`), so the
 * provider builds the count map once and exposes a stable `countFor(type, id)`
 * lookup. Indicators read it via {@link useUnresolvedCountLookup} — a plain
 * function, so it's safe to call inside a `.map` (no hook-in-loop), unlike a
 * per-anchor hook. Outside a provider (non-packet surfaces) the default returns 0.
 */
type CountFor = (anchorType: CommentAnchorType, anchorID: string) => number

const UnresolvedCommentsContext = createContext<CountFor>(() => 0)

export const UnresolvedCommentsProvider = ({
  comments,
  children,
}: {
  comments: Comment[]
  children: React.ReactNode
}) => {
  const countFor = useMemo<CountFor>(() => {
    const map = buildUnresolvedCountMap(comments)
    return (anchorType, anchorID) => map.get(unresolvedCountKey(anchorType, anchorID)) ?? 0
  }, [comments])

  return (
    <UnresolvedCommentsContext.Provider value={countFor}>
      {children}
    </UnresolvedCommentsContext.Provider>
  )
}

export const useUnresolvedCountLookup = (): CountFor => useContext(UnresolvedCommentsContext)
